import {
  CampaignReportSpecificationOutputDto,
  CustomReportDtoDestinationEnum,
  LookerQueryOutputDto,
  ReportDataDto,
  ReportDefaultsDto,
  ReportWithCampaignSpecificationsOutputDto,
} from '@kortxio/hub-api';
import { VisualizationQueryResult } from '@kortxio/hub-api';
import { ReportObjective } from 'features/reports/campaigns/components/ReportToolbar/types';
import { RequestState } from 'features/shared/request';
import { ObjectValues } from 'libs/types/types';

export type DateRange = {
  startDate: Date | undefined;
  endDate: Date | undefined;
};

export interface LookerReportWithCampaignSpecificationsOutputDto
  extends ReportWithCampaignSpecificationsOutputDto {
  queries?: Array<LookerQueryOutputDto>;
}

export const OverviewQueryPrefixes = {
  OVERVIEW: 'overview',
  OVERVIEW_CTV: 'overview_ctv',
} as const;
export type OverviewQueryPrefix = ObjectValues<typeof OverviewQueryPrefixes>;

export const ReportNames = {
  CROSS_CHANNEL: 'CROSS_CHANNEL',
  DIGITAL_OUT_OF_HOME: 'DIGITAL_OUT_OF_HOME',
  PROGRAMMATIC_FOR_XANDR: 'PROGRAMMATIC_FOR_XANDR',
  PROGRAMMATIC_FOR_XANDR_WITH_CTV: 'PROGRAMMATIC_FOR_XANDR_WITH_CTV',
  PROGRAMMATIC_FOR_TRADE_DESK: 'PROGRAMMATIC_FOR_TRADE_DESK',
  RICH_MEDIA_ENGAGEMENTS: 'RICH_MEDIA_ENGAGEMENTS',
  PAID_SOCIAL: 'PAID_SOCIAL',
  PAID_SEARCH: 'PAID_SEARCH',
  // Adfire Monitoring report is visible in the CampaignsById page, not the Reporting Suite page
  ADFIRE_MONITORING: 'ADFIRE_MONITORING',
} as const;
export type ReportName = ObjectValues<typeof ReportNames>;

export const DrillDownReportNames = {
  AUDIO: 'AUDIO',
  CTV: 'CTV',
  DISPLAY: 'DISPLAY',
  NATIVE: 'NATIVE',
  VIDEO: 'VIDEO',
} as const;
export type DrillDownReportName = ObjectValues<typeof DrillDownReportNames>;

export type SupportedReportName =
  | ObjectValues<Omit<typeof ReportNames, 'ADFIRE_MONITORING'>>
  | DrillDownReportName;
export function isSupportedReportName(val: string): val is SupportedReportName {
  if (val === ReportNames.ADFIRE_MONITORING) {
    return false;
  }

  return (
    Object.values<string>(ReportNames).includes(val) ||
    Object.values<string>(DrillDownReportNames).includes(val)
  );
}

export const ReportTabNamesAsParam = {
  AUDIO: 'audio',
  CROSS_CHANNEL: 'cross_channel',
  CTV: 'ctv',
  DIGITAL_OUT_OF_HOME: 'digital_out_of_home',
  DISPLAY: 'display',
  NATIVE: 'native',
  PAID_SEARCH: 'paid_search',
  PAID_SOCIAL: 'paid_social',
  PROGRAMMATIC_FOR_XANDR: 'overview',
  PROGRAMMATIC_FOR_XANDR_WITH_CTV: 'overview_ctv',
  PROGRAMMATIC_FOR_TRADE_DESK: 'programmatic_ttd',
  RICH_MEDIA_ENGAGEMENTS: 'rich_media_engagements',
  VIDEO: 'video',
} as const satisfies Record<SupportedReportName, string>;
export type SupportedReportTabNameAsParam = ObjectValues<
  typeof ReportTabNamesAsParam
>;

export const SupportedAdTypes = {
  AUDIO: 'audio',
  CTV: 'ctv',
  DIGITAL_OUT_OF_HOME: 'digital_out_of_home',
  BANNER: 'banner',
  NATIVE: 'native',
  RICH_MEDIA: 'rich_media',
  VIDEO: 'video',
} as const;
export type SupportedAdType = ObjectValues<typeof SupportedAdTypes>;

export const ReportTypes = {
  OVERVIEW: 'overview',
  CUSTOM: 'custom',
  DRILL_DOWN: 'drill-down',
  STANDARD: 'standard',
} as const;
export type ReportType = ObjectValues<typeof ReportTypes>;

export type SupportedReportMetadata = {
  id: string;
  reportName: SupportedReportName;
  tabNameAsParam: SupportedReportTabNameAsParam;
  label: string;
  adTypes?: SupportedAdType[];
  reportType: ReportType;
  availableObjectives: ReportObjective[] | undefined;
};

export type CustomReportMetadata = Omit<
  SupportedReportMetadata,
  'availableObjectives' | 'reportName' | 'tabNameAsParam'
> & {
  availableObjectives?: undefined;
  reportName: string;
  tabNameAsParam: string;
  url: string | undefined;
  destination: CustomReportDtoDestinationEnum | undefined;
};

export const isCustomReportMetadata = (
  val: ReportMetadata | undefined
): val is CustomReportMetadata => {
  return val?.reportType === ReportTypes.CUSTOM;
};

export type ReportMetadata = SupportedReportMetadata | CustomReportMetadata;

export type CampaignReportQueryResult = {
  name: string;
  data: ReportDataDto | undefined;
  request: RequestState;
};

export type RunQueryParams = {
  clientId: number;
  campaignId: number;
  id: string;
  queryId: string;
  queryName: string;
  startDate?: Date;
  endDate?: Date;
  budgetGroupIds?: number[];
  platformLineItemIds?: string[];
  xandrPixelIds?: string[];
  digitalOutOfHomeCampaignIds?: string[];
  limit?: number;
};

export type RunQueryParamsWithoutFilters = {
  clientId: number;
  campaignId: number;
  id: string;
  queryId: string;
};

export type CampaignReportsUiState = {
  searchForCampaignsInReportingSuite: string | undefined;
};

export type CampaignReportsState = {
  reports: {
    data: ReportWithCampaignSpecificationsOutputDto[] | undefined;
    request: RequestState;
  };
  selectedReport: ReportWithCampaignSpecificationsOutputDto | undefined;
  reportDefaults: {
    data: ReportDefaultsDto | undefined;
    request: RequestState;
  };
  reportSpecifications: {
    data: CampaignReportSpecificationOutputDto[] | undefined;
    request: RequestState;
  };
  queryResults: CampaignReportQueryResult[] | undefined;
  budgetGroupQueryResults: {
    data: ReportDataDto | undefined;
    request: RequestState;
  };
  pixelQueryResults: {
    data: ReportDataDto | undefined;
    request: RequestState;
  };
  isPixelSearchParamDirty: boolean;
  digitalOutOfHomeCampaignQueryResults: {
    data: ReportDataDto | undefined;
    request: RequestState;
  };
  adfireQueryResults: CampaignReportQueryResult[] | undefined;
  crossChannelPlatformQueryResults: VisualizationQueryResult | undefined;
  crossChannelAdTypeQueryResults: VisualizationQueryResult | undefined;
  crossChannelPixelQueryResults: VisualizationQueryResult | undefined;
  paidSearchPixelQueryResults: VisualizationQueryResult | undefined;
  paidSocialPixelQueryResults: VisualizationQueryResult | undefined;
  ui: CampaignReportsUiState;
};

export const OverviewGeoHeatmapChartMetrics = {
  Clicks: 'Clicks',
  Impressions: 'Impressions',
  Completions: 'Completions',
  Conversions: 'Conversions',
} as const;
export type OverviewGeoHeatmapChartMetric = ObjectValues<
  typeof OverviewGeoHeatmapChartMetrics
>;

export const PaidSearchGeoHeatmapChartMetrics = {
  Clicks: 'Clicks',
  Impressions: 'Impressions',
  Spend: 'Spend',
  SiteActivities: 'Site Activities',
} as const;
export type PaidSearchGeoHeatmapChartMetric = ObjectValues<
  typeof PaidSearchGeoHeatmapChartMetrics
>;

export const TradeDeskGeoHeatmapChartMetrics = {
  Clicks: 'Clicks',
  Impressions: 'Impressions',
  Spend: 'Spend',
  Conversions: 'Conversions',
} as const;
export type TradeDeskGeoHeatmapChartMetric = ObjectValues<
  typeof TradeDeskGeoHeatmapChartMetrics
>;

export const GeoViews = {
  Heatmap: 'HEATMAP',
  Region: 'REGION',
} as const;
export type GeoView = ObjectValues<typeof GeoViews>;
